import StorefrontPreviewFrame from '@/components/forms/DesignForm/StorefrontPreviewFrame'
import { buildDemoStorefrontUrl } from '@/components/forms/DesignForm/storefrontPreviewConfig'
import { WEBSITE_EDITOR_TARGET_SECTION_KEY } from '@/components/forms/DesignForm/websiteEditorTargetSection'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { AvailabilityQuickActionsCard } from '@/components/availability/AvailabilityQuickActionsCard'
import { InfoNotice, InfoNoticeAction } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import StorefrontStateBanner from '@/components/websites/StorefrontStateBanner'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { useAbility } from '@/lib/casl'
import {
  collectionQueries,
  designCollectionsFilters,
} from '@/lib/services/collections/collections.query'
import {
  designQueries,
  useActivateDesignMutation,
  useDisableDesignScheduleMutation,
  useEnableDesignScheduleMutation,
} from '@/lib/services/designs/designs.query'
import { domainQueries } from '@/lib/services/domains/domains.query'
import { locationFilters, locationQueries } from '@/lib/services/locations/locations.query'
import { useOrganization } from '@/lib/services/organizations/organizations.query'
import { useSeasonalStatus } from '@/lib/services/seasonal-content/seasonal-content.query'
import { storeQueries, useStore } from '@/lib/services/stores/stores.query'
import {
  availabilityChangeQueries,
  supportsAvailabilityChangesForOrganizationId,
} from '@/lib/services/availability-changes/availability-changes.query'
import { cn, getErrorMessage } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Link, notFound, useParams } from '@tanstack/react-router'
import {
  ArrowRight,
  CircleAlert,
  ExternalLink,
  Globe,
  Link2,
  LoaderCircle,
  MonitorSmartphone,
  Palette,
  Settings2,
  ShoppingCart,
  Wallpaper,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export function hasWebsiteAccess(ability) {
  return ability.can('view', 'stores')
}

export async function websiteOverviewLoader({ context, params }) {
  if (!hasWebsiteAccess(context.ability)) throw notFound()

  const queries = [
    context.queryClient.ensureQueryData(storeQueries.show({ organizationId: params.orgId })),
  ]

  if (context.ability.can('list', 'designs')) {
    queries.push(
      context.queryClient.ensureQueryData(designQueries.index({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(designQueries.default({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(storeQueries.themes({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(
        collectionQueries.index({
          organizationId: params.orgId,
          filters: designCollectionsFilters,
        })
      )
    )
  }

  if (context.ability.can('list', 'domains')) {
    queries.push(
      context.queryClient.ensureQueryData(domainQueries.index({ organizationId: params.orgId }))
    )
  }

  if (
    supportsAvailabilityChangesForOrganizationId(params.orgId) &&
    context.ability.can('list', 'availability-changes')
  ) {
    queries.push(
      context.queryClient.ensureQueryData(
        availabilityChangeQueries.index({ organizationId: params.orgId, filters: { past: false } })
      )
    )
  }

  if (
    supportsAvailabilityChangesForOrganizationId(params.orgId) &&
    context.ability.can('list', 'availability-changes') &&
    context.ability.can('list', 'locations')
  ) {
    queries.push(
      context.queryClient.ensureQueryData(
        locationQueries.index({
          organizationId: params.orgId,
          filters: locationFilters().get(),
        })
      )
    )
  }

  await Promise.all(queries)
}

function normalizeStorefrontUrl(value) {
  if (typeof value !== 'string' || value.trim() === '') {
    return null
  }

  try {
    const urlValue =
      value.startsWith('http://') || value.startsWith('https://') ? value : `https://${value}`
    const url = new URL(urlValue)
    url.pathname = ''
    url.search = ''
    url.hash = ''

    return url.toString().replace(/\/$/, '')
  } catch {
    return null
  }
}

function resolveVerifiedStorefrontUrl(domains = []) {
  const verifiedDomains = [...domains]
    .filter((domain) => domain?.verified_at && domain?.domain)
    .sort((a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0))
  const domain = verifiedDomains.find((domain) => domain?.is_primary) ?? verifiedDomains[0]

  return normalizeStorefrontUrl(domain?.domain)
}

export function resolveWebsiteStorefrontUrl({ domains = [], demoUrl = null } = {}) {
  return resolveVerifiedStorefrontUrl(domains) ?? demoUrl
}

function WebsiteTaskCard({
  orgId,
  href,
  to,
  params,
  search,
  targetSection,
  icon: Icon,
  title,
  description,
  className = '',
}) {
  const handleClick = () => {
    if (!targetSection || typeof window === 'undefined') {
      return
    }

    window.sessionStorage.setItem(WEBSITE_EDITOR_TARGET_SECTION_KEY, targetSection)
  }

  const linkProps = to
    ? {
        to,
        params: params ?? { orgId },
        search,
      }
    : {
        to: `/$orgId${href}`,
        params: { orgId },
        search,
      }

  return (
    <Link
      {...linkProps}
      onClick={handleClick}
      aria-label={`${title}: ${description}`}
      className={cn(
        'group flex items-center gap-3 rounded-xl border border-border/70 bg-card px-3 py-2.5 text-left transition-[border-color,background-color,box-shadow] hover:border-primary/25 hover:bg-primary/5 hover:shadow-sm',
        className
      )}
    >
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 text-interaction">
        <Icon className="size-4" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold leading-5 text-foreground">{title}</span>
        <Typography variant="caption" className="mt-0.5 block">
          {description}
        </Typography>
      </span>

      <ArrowRight className="size-4 shrink-0 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-interaction" />
    </Link>
  )
}

function StoreSettingLink({ link, icon: Icon, title, description, targetSection }) {
  const handleClick = () => {
    if (!targetSection || typeof window === 'undefined') {
      return
    }

    window.sessionStorage.setItem(WEBSITE_EDITOR_TARGET_SECTION_KEY, targetSection)
  }

  return (
    <Link
      {...link}
      onClick={handleClick}
      className="group flex items-center gap-3 border-t border-border/60 py-3 first:border-t-0"
    >
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground transition group-hover:bg-primary/10 group-hover:text-interaction">
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <Typography variant="caption" className="mt-0.5 block">
          {description}
        </Typography>
      </span>
      <ArrowRight className="size-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-interaction" />
    </Link>
  )
}

function StoreSettingsPanel({
  brandLink,
  checkoutLink,
  domainsLink,
  organizationId,
  productDisclaimerLink,
  verifiedDomainCount,
}) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-5">
        <div className="space-y-1">
          <Typography variant="h3">Store settings</Typography>
          <Typography variant="supporting" className="block">
            These apply across every website design.
          </Typography>
        </div>

        <AvailabilityQuickActionsCard organizationId={organizationId} className="mt-4" />

        <div className="mt-4">
          {brandLink ? (
            <StoreSettingLink
              link={brandLink}
              targetSection="brand"
              icon={Palette}
              title="Brand & layout"
              description="Logo, colors, fonts, and navigation"
            />
          ) : null}
          {productDisclaimerLink ? (
            <StoreSettingLink
              link={productDisclaimerLink}
              targetSection="product"
              icon={Settings2}
              title="Product disclaimer"
              description="Control storefront product wording"
            />
          ) : null}
          {checkoutLink ? (
            <StoreSettingLink
              link={checkoutLink}
              icon={ShoppingCart}
              title="Online checkout"
              description="Turn website tips on or off"
            />
          ) : null}
          {domainsLink ? (
            <StoreSettingLink
              link={domainsLink}
              icon={Globe}
              title="Address & domains"
              description={
                verifiedDomainCount > 0
                  ? `${verifiedDomainCount} verified ${verifiedDomainCount === 1 ? 'domain' : 'domains'}`
                  : 'Connect your storefront address'
              }
            />
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}

export default function WebsiteOverviewPage() {
  const { orgId } = useParams({ strict: false })
  const ability = useAbility()
  const isGlobalOrg = useIsGlobalOrg()
  const { data: organization } = useOrganization(orgId)
  const { data: store } = useStore()
  const [previewPage, setPreviewPage] = useState('home')
  const [previewDevice, setPreviewDevice] = useState('desktop')
  const canListDesigns = ability.can('list', 'designs')
  const canEditDesigns = ability.can('edit', 'designs')
  const { mutateAsync: disableDesignSchedule, isPending: isDisablingSchedule } =
    useDisableDesignScheduleMutation()
  const { mutateAsync: enableDesignSchedule, isPending: isEnablingSchedule } =
    useEnableDesignScheduleMutation()
  const { mutateAsync: activateDesign, isPending: isActivatingDesign } = useActivateDesignMutation()
  const { data: seasonalStatusResponse } = useSeasonalStatus({
    organizationId: orgId,
    enabled: canListDesigns && !isGlobalOrg,
  })

  const { data: designs = [] } = useQuery({
    ...designQueries.index({ organizationId: orgId }),
    enabled: canListDesigns,
  })
  const { data: domains = [] } = useQuery({
    ...domainQueries.index({ organizationId: orgId }),
    enabled: ability.can('list', 'domains'),
  })

  const activeDesignId =
    Number(store?.active_scheduled_design_id) || Number(store?.active_design_id) || null
  const activeDesign = designs.find((design) => Number(design?.id) === activeDesignId) ?? null
  const defaultDesign = designs.find((design) => design?.is_default) ?? null
  const previewDesign = activeDesign ?? defaultDesign ?? null
  const previewDesignId = previewDesign?.id ?? null
  const shouldLoadActiveDesignDetail = Boolean(previewDesignId && !previewDesign?.is_default)
  const { data: activeDesignDetail = null } = useQuery({
    ...designQueries.show({ organizationId: orgId, designId: previewDesignId }),
    enabled: canListDesigns && shouldLoadActiveDesignDetail,
  })
  const { data: defaultDesignDetail = null } = useQuery({
    ...designQueries.default({ organizationId: orgId }),
    enabled: canListDesigns && Boolean(defaultDesign?.id),
  })
  const { data: themes = [] } = useQuery({
    ...storeQueries.themes({ organizationId: orgId }),
    enabled: canListDesigns,
  })
  const { data: collections = [] } = useQuery({
    ...collectionQueries.index({
      organizationId: orgId,
      filters: designCollectionsFilters,
    }),
    enabled: canListDesigns,
  })
  const detailedPreviewDesign = previewDesign?.is_default
    ? (defaultDesignDetail ?? previewDesign)
    : (activeDesignDetail ?? null)
  const previewFrameDesign = detailedPreviewDesign
  const previewFrameDefaultDesign = defaultDesignDetail ?? defaultDesign
  const hasSeasonalDesignLive = Boolean(activeDesign && !activeDesign?.is_default)
  const hasActiveSeasonalSchedule = Boolean(
    hasSeasonalDesignLive && Number(store?.active_scheduled_design_id) === Number(activeDesign?.id)
  )
  const verifiedDomainCount = domains.filter((domain) => !!domain?.verified_at).length
  const demoUrl = buildDemoStorefrontUrl(organization?.demo_id, import.meta.env.VITE_DEMO_URL)
  const storefrontUrl = resolveWebsiteStorefrontUrl({ domains, demoUrl })
  const seasonalStatus = seasonalStatusResponse?.data
  const defaultDesignLink = {
    to: '/$orgId/website/builder',
    params: { orgId },
    search: { section: 'brand-layout' },
  }
  const liveSeasonalUpdateLink =
    hasSeasonalDesignLive && activeDesign?.id
      ? {
          to: '/$orgId/designs/$designId',
          params: { orgId, designId: String(activeDesign.id) },
        }
      : null
  const domainsLink = ability.can('list', 'domains')
    ? { to: '/$orgId/website/domains', params: { orgId } }
    : null
  const brandLink = {
    to: '/$orgId/website/builder',
    params: { orgId },
    search: { section: 'brand-layout' },
  }
  const productDisclaimerLink = brandLink
  // Checkout options change what customers pay, so they follow checkout-settings, not website design access.
  const checkoutLink = ability.can('list', 'checkout-settings')
    ? { to: '/$orgId/settings/checkout', params: { orgId } }
    : null
  const actionCards = [
    {
      title: 'Everyday design',
      description: 'Edit the look and content customers see between seasonal updates.',
      href: '/website/builder',
      search: { section: 'brand-layout' },
      targetSection: 'brand',
      icon: Palette,
      show: ability.can('list', 'designs'),
    },
    {
      title: 'Seasonal website updates',
      description: 'Preview, schedule, and customize seasonal changes.',
      href: '/designs',
      icon: Wallpaper,
      show: ability.can('list', 'designs'),
    },
    {
      title: 'Domains',
      description: 'Connect and manage your storefront address.',
      href: '/website/domains',
      icon: Globe,
      show: ability.can('list', 'domains'),
    },
    {
      title: 'URL Redirects',
      description: 'Keep old website pages working after moving your website.',
      href: '/website/url-redirects',
      icon: Link2,
      show: ability.can('list', 'website-redirects'),
    },
  ].filter((card) => card.show)

  const handleOptOutSeasonalUpdate = async () => {
    if (!activeDesign?.id) {
      return
    }

    try {
      let returnedToEverydayWebsite = false

      if (hasActiveSeasonalSchedule) {
        await disableDesignSchedule(activeDesign.id)
        returnedToEverydayWebsite = true
      } else if (defaultDesign?.id) {
        await activateDesign(defaultDesign.id)
        returnedToEverydayWebsite = true
      }

      if (!returnedToEverydayWebsite) {
        return
      }

      toast.success('Returned to everyday website')
    } catch (error) {
      toast.error('Unable to turn off seasonal update', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleActivateSkippedUpdate = async () => {
    const skippedDesignId = seasonalStatus?.skipped?.design_id

    if (!skippedDesignId) {
      return
    }

    try {
      await enableDesignSchedule(skippedDesignId)
      toast.success(`${seasonalStatus.skipped.name} is live`)
    } catch (error) {
      toast.error('Unable to activate seasonal update', {
        description: getErrorMessage(error),
      })
    }
  }

  let storefrontStateAction = null

  if (canEditDesigns) {
    if (hasSeasonalDesignLive) {
      storefrontStateAction = (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={
            isDisablingSchedule || isEnablingSchedule || isActivatingDesign || !defaultDesign?.id
          }
          onClick={handleOptOutSeasonalUpdate}
        >
          Back to everyday
        </Button>
      )
    } else if (seasonalStatus?.skipped) {
      storefrontStateAction = (
        <Button
          type="button"
          size="sm"
          disabled={isDisablingSchedule || isEnablingSchedule || isActivatingDesign}
          onClick={handleActivateSkippedUpdate}
        >
          Activate {seasonalStatus.skipped.name}
        </Button>
      )
    } else {
      storefrontStateAction = (
        <Button asChild size="sm">
          <Link {...defaultDesignLink}>Edit everyday design</Link>
        </Button>
      )
    }
  }

  if (isGlobalOrg) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          icon={MonitorSmartphone}
          title="Website"
          description="Manage how your storefront looks, what your key pages say, and where customers find it."
        />

        <InfoNotice
          title="Global Seasonal Content"
          tone="global"
          icon={<CircleAlert />}
          action={
            <InfoNoticeAction to="/$orgId/seasonal-content" params={{ orgId }}>
              Open seasonal content
            </InfoNoticeAction>
          }
        >
          Curate reusable seasonal heroes and default schedules here. Impersonate a florist when you
          need to manage a live florist storefront.
        </InfoNotice>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-5">
        <AdminPageHeader
          icon={MonitorSmartphone}
          title="Website"
          description="Manage how your storefront looks, what your key pages say, and where customers find it."
          actions={
            storefrontUrl ? (
              <AdminHeaderActionButton
                asChild
                variant="outline"
                className="shrink-0 bg-background/60"
              >
                <a href={storefrontUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="size-4" />
                  View Storefront
                </a>
              </AdminHeaderActionButton>
            ) : null
          }
        />

        <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(22rem,1fr)] xl:items-start">
          <aside className="order-2 min-w-0 space-y-5">
            <StoreSettingsPanel
              brandLink={canListDesigns ? brandLink : null}
              checkoutLink={checkoutLink}
              domainsLink={domainsLink}
              organizationId={orgId}
              productDisclaimerLink={canListDesigns ? productDisclaimerLink : null}
              verifiedDomainCount={verifiedDomainCount}
            />

            <Card>
              <CardContent className="p-4">
                <Typography variant="h4">What would you like to change?</Typography>

                <div className="mt-3 space-y-2">
                  {actionCards.map((card) => (
                    <WebsiteTaskCard key={card.title} orgId={orgId} {...card} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </aside>

          <main className="order-1 min-w-0 space-y-5">
            {ability.can('list', 'designs') && previewDesign ? (
              <Card className="overflow-hidden">
                <CardContent className="space-y-4 p-4 sm:p-5">
                  <Typography variant="h3">Live storefront snapshot</Typography>

                  <StorefrontStateBanner
                    action={storefrontStateAction}
                    activeDesign={hasSeasonalDesignLive ? activeDesign : null}
                    activeStatus={seasonalStatus?.active}
                    nextStatus={seasonalStatus?.next}
                    skippedStatus={seasonalStatus?.skipped}
                  />

                  <div className="hidden lg:block">
                    {previewFrameDesign ? (
                      <StorefrontPreviewFrame
                        design={previewFrameDesign}
                        defaultDesign={previewFrameDefaultDesign}
                        organization={organization}
                        themes={themes}
                        collections={collections}
                        previewPage={previewPage}
                        onPreviewPageChange={setPreviewPage}
                        previewDevice={previewDevice}
                        onPreviewDeviceChange={setPreviewDevice}
                        syncDraft={false}
                        className="h-[min(820px,calc(100dvh-8rem))] min-h-[600px]"
                        viewportClassName="h-full"
                      />
                    ) : (
                      <div className="flex h-[min(820px,calc(100dvh-8rem))] min-h-[600px] items-center justify-center rounded-xl border border-border bg-card">
                        <div className="space-y-3 text-center">
                          <span className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-interaction">
                            <LoaderCircle className="size-5 animate-spin" />
                          </span>
                          <div className="space-y-1">
                            <Typography variant="h4">Preparing storefront snapshot</Typography>
                            <Typography variant="supporting">
                              Loading the current design before showing the preview.
                            </Typography>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-border/70 bg-muted/30 p-4 lg:hidden">
                    <div className="space-y-2">
                      <Typography variant="h4">Preview on a larger screen.</Typography>
                      <Typography variant="body" tone="muted" className="block">
                        The live preview is available on wider desktop screens. On this screen, jump
                        straight to the storefront or open the editor.
                      </Typography>
                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      {storefrontUrl ? (
                        <Button asChild size="sm">
                          <a href={storefrontUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="size-4" />
                            View Storefront
                          </a>
                        </Button>
                      ) : null}
                      <Button asChild size="sm" variant="outline">
                        <Link {...(liveSeasonalUpdateLink ?? defaultDesignLink)}>
                          {liveSeasonalUpdateLink ? 'Edit seasonal update' : 'Edit everyday design'}
                        </Link>
                      </Button>
                      {liveSeasonalUpdateLink ? (
                        <Button asChild size="sm" variant="ghost">
                          <Link {...defaultDesignLink}>Edit everyday design</Link>
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex min-h-80 items-center justify-center p-6 text-center">
                  <div className="max-w-md space-y-3">
                    <span className="mx-auto inline-flex size-12 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 text-interaction">
                      <Wallpaper className="size-6" />
                    </span>
                    <Typography variant="h3">No storefront preview yet</Typography>
                    <Typography variant="body" tone="muted" className="block">
                      Your live storefront preview will appear here once the everyday design is
                      ready.
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      </div>
    </>
  )
}
